import { get, post } from "@/shared/utils/fetchApi";
import { SignUpResponse, OnboardingStatus } from "../types/onboarding-types";

/**
 * Service for handling user sign-up and onboarding
 */
class SignUpService {
  /**
   * Register a new user with email
   * @param email - User email address
   * @param locale - User locale (optional, default: pt-BR)
   * @returns Promise with sign-up response
   */
  public static async signUp(
    email: string,
    locale?: string,
  ): Promise<SignUpResponse> {
    try {
      // POST /auth/signup retorna `{message}` flat (201 novo/já-cadastrado, 200
      // convite pendente) — normaliza pro shape `{success,message}`.

      const data = await post<{ message: string }>("/auth/signup", {
        email,
        locale,
      });
      if (!data) return { success: false, message: "Failed to sign up" };
      return { success: true, message: data.message };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorResponse = error.response?.data;

      return {
        success: false,
        message: errorResponse?.message || "Failed to sign up",
        code: errorResponse?.code,
        actions: errorResponse?.actions,
      };
    }
  }

  /**
   * Check the onboarding status of the authenticated user
   * @returns Promise with onboarding status
   */
  public static async checkOnboardingStatus(): Promise<{
    success: boolean;
    data?: OnboardingStatus;
    message?: string;
  }> {
    try {
      // GET /auth/onboarding/status retorna OnboardingStatus raw (snake_case).
      // legacy: { success, data: OnboardingStatus }.

      const data = await get<OnboardingStatus>("/auth/onboarding/status");
      return { success: true, data };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to check onboarding status";

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Complete the onboarding of a user that already belongs to an organization
   * (invited user): the flow ends after profile/survey, without creating an
   * organization (bug 84095).
   */
  public static async completeOnboarding(): Promise<{
    success: boolean;
    data?: OnboardingStatus;
    message?: string;
  }> {
    try {
      const data = await post<OnboardingStatus>(
        "/auth/onboarding/complete",
        {},
      );
      return { success: true, data };
    } catch (error: unknown) {
      // Erros do fetchApi são ApiError (Error com a mensagem do backend)
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : "Failed to complete onboarding";

      return {
        success: false,
        message: errorMessage,
      };
    }
  }
}

export default SignUpService;
